import { useEffect, useRef, useState } from 'react';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as Logomark } from '@rocco/icons/svg/logo-new-white.svg';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { ProductLaunchCountdown } from '~/ui-rocco/components/product-launch-countdown/ProductLaunchCountdown';
import { useProductLaunchCountdownEnabled } from '~/ui-rocco/components/product-launch-countdown/use-product-launch-countdown-enabled';
import { useHeroMarket } from './hero-markets';
import { button } from '~/ui-rocco/button-system';
import { useIsHomePage } from '../use-is-home-page';
import {
    PauseIcon,
    PlayIcon,
} from '~/ui-rocco/pages/detail-shared/magic-island/modes/PaginationIslandContent';

/**
 * Homepage hero (BC-4479).
 *
 * A static, per-market project banner (`useHeroMarket`) under a large headline,
 * with a subtle parallax as the page scrolls and a hyperlinked "Featured
 * Project" credit in the bottom corner. The banner is not locked to the
 * viewport — it keeps the Figma frame's natural height (1920×1373 ⇒ 71.51vw),
 * so the tagline/CTA lower in the hero and the intro section beneath reveal as
 * you scroll. Replaces the previous full-bleed hero video.
 */
const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    });
    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReduced(query.matches);
        const onChange = () => setReduced(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    return reduced;
};

/**
 * Plays the banner video unless the reader prefers reduced motion or has
 * paused it. Playback starts from an effect rather than `autoPlay`: the server
 * cannot know the reduced-motion preference, and an `autoplay` attribute in its
 * HTML starts the video before hydration could stop it.
 */
const useBannerVideo = (reducedMotion: boolean) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [pausedByReader, setPausedByReader] = useState<boolean | null>(null);
    const playing = !(pausedByReader ?? reducedMotion);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (!playing) {
            video.pause();
            return;
        }
        // A refused play (no source on a phone, data saver) leaves the poster.
        video.play().catch(() => undefined);
    }, [playing]);

    const togglePlaying = () => setPausedByReader(playing);
    return { videoRef, playing, togglePlaying };
};

/**
 * Subtle background parallax tied to the hero's scroll position. Writes the
 * transform straight onto the banner image in a rAF-throttled scroll handler —
 * no React state, so the page doesn't re-render on every frame (which made the
 * effect stutter). The banner is rendered 12% taller than the section, so the
 * shift is clamped to that slack to avoid revealing an edge; disabled entirely
 * under reduced motion.
 */
const useHeroParallax = (
    containerRef: React.RefObject<HTMLElement | null>,
    targetRef: React.RefObject<HTMLElement | null>,
    enabled: boolean,
) => {
    useEffect(() => {
        const container = containerRef.current;
        const target = targetRef.current;
        if (!container || !target) return;
        if (!enabled) {
            target.style.transform = '';
            return;
        }

        let raf = 0;
        const update = () => {
            raf = 0;
            const rect = container.getBoundingClientRect();
            const maxShift = rect.height * 0.06;
            const next = Math.min(Math.max(-rect.top * 0.18, 0), maxShift);
            target.style.transform = `translate3d(0, ${next.toFixed(1)}px, 0)`;
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [containerRef, targetRef, enabled]);
};

/**
 * The headline is specced as Helvetica Neue Light in Figma — not the brand
 * `font-sans` (Neue Haas Grotesk), which renders noticeably heavier. Apply the
 * Helvetica Neue stack directly so it matches the design.
 */
const HEADLINE_FONT =
    'neue-haas-grotesk-text, "Helvetica Neue", Helvetica, Arial, sans-serif';

/** The hero headline, split into two lines for the masked line reveal. */
const HEADLINE_LINES = ['Where beautifully', 'designed spaces begin'] as const;

/** Detail-closing headline — same split, with the design's full stop. */
const DETAIL_HEADLINE_LINES = [
    'Where beautifully',
    'designed spaces begin.',
] as const;

type HeroHeadlineAnimationState = 'idle' | 'pending' | 'played';

/**
 * Keeps the hero reveal as a one-shot animation for each browser runtime. This
 * avoids replaying the entry motion if the hero subtree re-mounts during
 * initial hydration.
 */
let heroHeadlineAnimationState: HeroHeadlineAnimationState = 'idle';

const reserveHeroHeadlineAnimation = () => {
    if (heroHeadlineAnimationState !== 'idle') {
        return false;
    }
    heroHeadlineAnimationState = 'pending';
    return true;
};

const commitHeroHeadlineAnimation = () => {
    heroHeadlineAnimationState = 'played';
};

const releaseHeroHeadlineAnimationReservation = () => {
    if (heroHeadlineAnimationState === 'pending') {
        heroHeadlineAnimationState = 'idle';
    }
};

/** Resets the one-shot state between tests. */
export const __resetHeroHeadlineAnimationStateForTests = () => {
    heroHeadlineAnimationState = 'idle';
};

/**
 * Plays the hero reveal only for the first client mount, and keeps it static
 * afterwards so the heading does not replay during hydration remounts.
 */
const useHeroHeadlineRevealAnimation = (reducedMotion: boolean) => {
    const [shouldAnimate, setShouldAnimate] = useState(false);

    useEffect(() => {
        if (reducedMotion) {
            return;
        }
        if (!reserveHeroHeadlineAnimation()) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            commitHeroHeadlineAnimation();
            setShouldAnimate(true);
        });

        return () => {
            window.cancelAnimationFrame(frameId);
            releaseHeroHeadlineAnimationReservation();
        };
    }, [reducedMotion]);

    return shouldAnimate;
};

/**
 * Headline with an Exo-Ape-style line reveal: each line sits in an
 * overflow-hidden mask and slides up from below, the second line a beat after
 * the first. Pure transform under a mask (no opacity) for a crisp wipe. Honours
 * `prefers-reduced-motion` by rendering the lines in place with no movement.
 */
const RevealHeadline = ({
    className,
    style,
    animate,
    lines = HEADLINE_LINES,
    as: Heading = 'h1',
}: {
    className: string;
    style?: React.CSSProperties;
    animate: boolean;
    lines?: readonly string[];
    /**
     * The heading level. `h1` on the homepage, where this is the page's own
     * headline; the detail-closing band passes `h2`, since the detail page it
     * sits under already has an h1 and a second one would compete with it.
     */
    as?: 'h1' | 'h2';
}) => (
    <Heading className={className} style={style}>
        {lines.map((line, index) => (
            <span
                key={line}
                className="-mb-[0.12em] block overflow-hidden pb-[0.12em]"
            >
                <span
                    className={`${animate ? 'hero-line-rise ' : ''}block ${animate ? 'will-change-transform' : ''}`}
                    style={{ animationDelay: `${index * 120}ms` }}
                >
                    {line}
                </span>
            </span>
        ))}
    </Heading>
);

export interface HeroSectionProps {
    /**
     * `home` (default) — the full homepage hero: headline mid-frame, tagline +
     * Get Started CTA, featured-project credit.
     * `detail` — the detail-closing treatment (Figma 1679:57790): ArchiPro
     * logomark top-left, a hairline + "(About ArchiPro)" eyebrow above the
     * lowered headline (with full stop), no tagline / CTA / credit.
     */
    variant?: 'home' | 'detail';
}

export const HeroSection = ({ variant = 'home' }: HeroSectionProps = {}) => {
    const isDetail = variant === 'detail';
    const { handleCTAClick, handleLoginClick } = useCTAHandlers('HomePageHero');
    const { image, video, featuredProject } = useHeroMarket();
    const showLaunchCountdown = useProductLaunchCountdownEnabled();

    const reducedMotion = usePrefersReducedMotion();
    const shouldAnimateHeadline = useHeroHeadlineRevealAnimation(reducedMotion);
    const isHomePage = useIsHomePage();
    const showVideo = isHomePage && Boolean(video) && !isDetail;
    const bannerVideo = useBannerVideo(reducedMotion);
    const sectionRef = useRef<HTMLDivElement>(null);
    const bannerRef = useRef<HTMLDivElement>(null);
    const bannerImageRef = useRef<HTMLImageElement>(null);
    useHeroParallax(
        sectionRef,
        isHomePage ? bannerRef : bannerImageRef,
        !reducedMotion,
    );

    const onGetStarted = () => {
        handleCTAClick('startProject');
        handleLoginClick();
    };

    const renderCredit = (className: string) => (
        <RoccoLink
            to={featuredProject.href}
            className={`font-sans leading-tight font-medium opacity-60 transition-opacity hover:opacity-100 ${className}`}
        >
            <span className="block">
                Featured Project: {featuredProject.name}
            </span>
            <span className="block">by {featuredProject.architect}</span>
        </RoccoLink>
    );

    return (
        <section className="relative w-full bg-black text-white focus-ring-on-dark">
            {/* Desktop — banner with parallax + overlay anchored to the
                1920×1373 frame. */}
            <div
                ref={sectionRef}
                className="relative hidden overflow-hidden md:block md:h-[71.5104vw] lg:h-[85.8125rem]"
            >
                {/* Banner rendered 12% taller so the parallax never shows an
                    edge. The market's video plays over its image, which is
                    the poster until the first frame lands. */}
                {!isHomePage && (
                    <img
                        ref={bannerImageRef}
                        src={image}
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        className="absolute inset-x-0 top-[-6%] h-[112%] w-full object-cover will-change-transform"
                    />
                )}
                {isHomePage && (
                    <div
                        ref={bannerRef}
                        aria-hidden="true"
                        className="absolute inset-x-0 top-[-6%] h-[112%] w-full will-change-transform"
                    >
                        {video && showVideo ? (
                            <video
                                ref={bannerVideo.videoRef}
                                className="size-full object-cover"
                                poster={image}
                                muted
                                loop
                                playsInline
                                // Loads once playback starts, so the poster is
                                // the banner's only first-paint download.
                                preload="none"
                            >
                                {/* Desktop widths only: the phone banner is the
                                still image, so a phone fetches no video. */}
                                <source
                                    src={video.webm}
                                    type="video/webm"
                                    media="(min-width: 48rem)"
                                />
                                <source
                                    src={video.mp4}
                                    type="video/mp4"
                                    media="(min-width: 48rem)"
                                />
                            </video>
                        ) : (
                            <img
                                src={image}
                                alt=""
                                loading="eager"
                                className="size-full object-cover"
                            />
                        )}
                    </div>
                )}

                {/* Pre-launch darkening (Figma `4385:20701`): the design lays
                    rgba(0,0,0,0.2) over the whole banner so the countdown
                    digits read against the bright sky. Scoped to the clock so
                    NZ/AU keep their signed-off grade. */}
                {showLaunchCountdown && (
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-black/20"
                    />
                )}

                {/* Bottom gradient for legibility (Figma `1420:30855`):
                    #131313 fading up to transparent over the lower ~43%. */}
                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-[42.97%] bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
                />

                {/* Overlay anchors to the centred 1920 container so the frame's
                    offsets hold on wider screens (the banner stays full-bleed). */}
                <div className="absolute inset-0 mx-auto w-full max-w-(--ap-page-width)">
                    {isDetail ? (
                        <>
                            {/* ArchiPro logomark top-left (Figma 1679:57794). */}
                            <Logomark
                                aria-hidden="true"
                                className="absolute h-auto text-white md:w-[11.44vw] lg:w-[13.728rem]"
                                style={{ left: '2.61%', top: '12.34%' }}
                            />
                            {/* Eyebrow above the headline (1679:57795). */}
                            <p
                                className="absolute inset-x-[2.97%] m-0 font-sans leading-[1.3] text-white md:text-[1.146vw] lg:text-[1.3752rem]"
                                style={{ top: '53.93%' }}
                            >
                                (About ArchiPro)
                            </p>
                        </>
                    ) : null}

                    {/* US product-launch clock, upper right of the banner
                        (Figma `4393:20894`: 1157.95/1920 × 299.09/1373). */}
                    {showLaunchCountdown && (
                        <div
                            className="absolute text-white"
                            style={{ left: '60.31%', top: '21.78%' }}
                        >
                            <ProductLaunchCountdown />
                        </div>
                    )}

                    <RevealHeadline
                        className="absolute m-0 font-normal text-[#e5e5e5] md:text-[6.25vw] md:leading-[6.77vw] md:tracking-[-0.3vw] lg:text-[7.5rem] lg:leading-[8.124rem] lg:tracking-[-0.36rem]"
                        animate={shouldAnimateHeadline}
                        lines={
                            isDetail ? DETAIL_HEADLINE_LINES : HEADLINE_LINES
                        }
                        as={isDetail ? 'h2' : 'h1'}
                        style={{
                            left: '2.97%',
                            fontFamily: HEADLINE_FONT,
                            ...(isDetail || !isHomePage
                                ? { top: isDetail ? '59.87%' : '50%' }
                                : {
                                      // Sits 80px above the fold on any
                                      // screen, never lower than just above
                                      // the tagline row at 89.51%.
                                      bottom: 'max(calc(100% - 100svh + 80px), 12.5%)',
                                  }),
                        }}
                    />

                    {isDetail ? null : (
                        <>
                            <div
                                className="absolute flex items-center justify-between"
                                style={{
                                    left: '24.64%',
                                    top: '89.51%',
                                    width: '44.65%',
                                }}
                            >
                                <p
                                    className={cn(
                                        'm-0 font-sans font-medium md:text-[1.14vw] md:leading-[1.426vw] lg:text-[1.368rem] lg:leading-[1.7112rem]',
                                        isHomePage
                                            ? 'text-[#e5e5e5]'
                                            : 'text-[#fafafa]',
                                    )}
                                    style={{ width: '75.46%' }}
                                >
                                    ArchiPro is the all-in-one platform helping
                                    people find trusted products and
                                    professionals for their building project
                                </p>
                                <button
                                    type="button"
                                    onClick={onGetStarted}
                                    className={
                                        isHomePage
                                            ? button(
                                                  'primary-inverse',
                                                  'l',
                                                  // The tagline's grey, not white.
                                                  'shrink-0 bg-[#e5e5e5] hover:bg-[#f2f2f2]',
                                              )
                                            : 'flex shrink-0 items-center justify-center rounded-[1.375rem] bg-[#f2f2f2] font-sans font-normal whitespace-nowrap text-[#141414] transition-colors hover:bg-white md:px-[1.71vw] md:py-[0.57vw] md:text-[1.14vw] lg:px-[2.052rem] lg:py-[0.684rem] lg:text-[1.368rem]'
                                    }
                                >
                                    Get Started
                                </button>
                            </div>

                            <div
                                className="absolute text-right md:text-[0.833vw] lg:text-[1rem]"
                                style={{ right: '1.93%', bottom: '3.06%' }}
                            >
                                {renderCredit('text-white')}
                            </div>

                            {/* Opposite the credit: the looping banner
                                video's pause control. */}
                            {showVideo && (
                                <button
                                    type="button"
                                    onClick={bannerVideo.togglePlaying}
                                    aria-label={
                                        bannerVideo.playing
                                            ? 'Pause background video'
                                            : 'Play background video'
                                    }
                                    className="absolute flex size-10 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                                    style={{ left: '1.93%', bottom: '3.06%' }}
                                >
                                    {bannerVideo.playing ? (
                                        <PauseIcon className="size-4" />
                                    ) : (
                                        <PlayIcon className="ml-0.5 size-4" />
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Mobile — full-screen banner; the architect credit moves to a
                smaller caption just below the image (BC-4482). Uses a stable
                `svh` (not `dvh`) so the banner doesn't resize/jump as the mobile
                address bar shows/hides on scroll (BC-4486). */}
            <div className="md:hidden">
                <div className="relative h-[100svh] overflow-hidden">
                    <img
                        src={image}
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        className="absolute inset-0 size-full object-cover"
                    />
                    {/* Same pre-launch darkening as the desktop banner. */}
                    {showLaunchCountdown && (
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 bg-black/20"
                        />
                    )}
                    <div
                        aria-hidden="true"
                        className="absolute inset-x-0 bottom-0 h-[42.97%] bg-linear-to-t from-[#131313] from-[6.207%] to-transparent to-[70.169%] opacity-[0.89]"
                    />
                    {/* Pre-launch clock pinned near the top of the banner,
                        just under the nav band, so the bottom text group
                        stays headline + blurb + CTA only. */}
                    {showLaunchCountdown && (
                        <div className="absolute inset-x-0 top-20 px-6">
                            <ProductLaunchCountdown />
                        </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 flex flex-col gap-4 px-6 pb-10">
                        <RevealHeadline
                            className="m-0 leading-[1.05] font-normal tracking-[-0.01em] text-[#e5e5e5] max-md:text-[2.3288rem]"
                            animate={shouldAnimateHeadline}
                            as={isDetail ? 'h2' : 'h1'}
                            style={{ fontFamily: HEADLINE_FONT }}
                        />
                        <p className="m-0 font-sans leading-snug font-medium text-[#fafafa] max-md:text-sm">
                            ArchiPro is the all-in-one platform helping people
                            find trusted products and professionals for their
                            building project
                        </p>
                        <button
                            type="button"
                            onClick={onGetStarted}
                            className="w-fit rounded-[1rem] bg-[#f2f2f2] px-7 py-3 font-sans font-normal whitespace-nowrap text-[#141414]"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
                {/* Credit on a white strip so it blends into the section
                    below (BC-4482). */}
                {/* White strip inside the dark section — reset the ring token
                    so the credit link keeps the near-black ring. */}
                <div className="bg-white px-6 py-3 text-right text-[0.5951rem] focus-ring-on-light">
                    {renderCredit('text-[#1f1f21]')}
                </div>
            </div>
        </section>
    );
};
